import React from 'react';
import { useTranslation } from 'react-i18next';

import 'assets/sportsbook/styles/spinner.scss';
import DynamicSpinner from 'shared/utils/loader';
import { getSportColor, getSportIdForImagePath, getSportOnColor, getSportTextColorClasses } from 'shared/utils/sport-colors';
import { withBasePath } from 'shared/utils/runtimeBasePath';
import { UPCOMING_EVENTS_LIST_SIZE } from 'features/events/api';
import { useSchedulesQuery, useAllUpcomingEventsQueries, flattenScheduleUpcomingToEvents } from 'features/events/queries';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import type { Schedule } from 'features/events/types';
import { useTicketSlipStore } from 'features/tickets/store';
import { useMobileTabsContext } from 'app/stores/mobile-tabs-store';
import { useScheduleStatus } from 'features/events/hooks/useScheduleStatus';

const DynamicSpinnerComp = DynamicSpinner as unknown as React.FC<{ status?: boolean }>;

function formatCountdown(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

type ScheduleButtonItemProps = {
  schedule: Schedule;
  isActive: boolean;
  isLast: boolean;
  iconErrorMap: Record<string, boolean>;
  setIconErrorMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onScheduleClick: (scheduleId: number | string) => void;
  sportIdsWithLocalPngIcon: Set<number>;
};

function ScheduleButtonItem({
  schedule,
  isActive,
  isLast,
  iconErrorMap,
  setIconErrorMap,
  onScheduleClick,
  sportIdsWithLocalPngIcon,
}: ScheduleButtonItemProps) {
  const { t } = useTranslation();
  const sportObj = schedule.sport;
  const sport_id = sportObj?.sport_id != null ? parseInt(String(sportObj.sport_id), 10) : null;
  const n_runners = sportObj?.n_runners != null ? parseInt(String(sportObj.n_runners), 10) : 0;
  const { phase, countdownSeconds } = useScheduleStatus(schedule?.schedule_id ?? null, sport_id);

  const isRacing =
    sportObj?.is_racing === true ||
    sportObj?.is_racing === 1 ||
    parseInt(String(sportObj?.is_racing)) === 1;

  const sportColor = sport_id != null ? getSportColor(sport_id) : 'var(--muted)';
  const textColorSport = sport_id != null ? getSportTextColorClasses(sport_id) : '';
  const normalizedSportIdForIcon = sport_id != null ? getSportIdForImagePath(sport_id) : 0;
  const iconKey = `${schedule.schedule_id}_${sport_id ?? 0}_${n_runners}`;
  const hasIconError = !!iconErrorMap[iconKey];

  const baseballBallSrc = sport_id === 40 ? withBasePath('/images/baseball/ball-big.png') : null;
  const footballIconSrc = (sport_id === 50 || sport_id === 51) ? withBasePath('/images/football/sport-icon.svg') : null;
  const localIconSrc = !baseballBallSrc && !footballIconSrc && sport_id != null && sportIdsWithLocalPngIcon.has(normalizedSportIdForIcon)
    ? withBasePath(`/images/last_results_class_icon_${normalizedSportIdForIcon}.png`)
    : null;
  const iconSrc = baseballBallSrc || footballIconSrc || localIconSrc;

  const buttonLabel = schedule.label ?? `Schedule ${schedule.schedule_id}`;

  const sportKeyForLabel = sport_id === 51 ? 50 : sport_id;
  const translatedSportName = sport_id != null ? t('SPORT_' + sportKeyForLabel) : '';
  const sportName =
    translatedSportName && translatedSportName !== 'SPORT_' + sportKeyForLabel
      ? translatedSportName
      : (sportObj?.description ?? sportObj?.alias ?? buttonLabel);
  const desktopLabel =
    isRacing && n_runners > 0 ? `${n_runners} ${sportName}` : buttonLabel;

  return (
    <button
      type="button"
      data-schedule-id={schedule.schedule_id}
      data-sport-id={sport_id ?? ''}
      data-active={isActive}
      onClick={() => onScheduleClick(schedule.schedule_id)}
      className={`
        flex flex-col md:flex-row
        items-center justify-center
        gap-1 md:gap-2
        flex-1 min-w-[90px] md:min-w-[120px] flex-shrink-0
        h-full min-h-0 px-3 md:px-4 py-4 md:py-7 xl:py-8
        rounded-none overflow-hidden
        transition-colors duration-200 transition-opacity duration-200
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
        ${!isLast ? 'border-r border-border' : ''}
        ${textColorSport}
      `}
      style={{
        scrollSnapAlign: 'start',
        backgroundColor: sportColor,
        opacity: isActive ? 1 : 0.7,
        ...(isActive ? { marginBottom: '-1px', borderBottom: `2px solid ${sportColor}` } : {}),
      }}
      title={buttonLabel}
      aria-label={buttonLabel}
      aria-current={isActive ? 'true' : undefined}
    >
      {(!iconSrc || hasIconError) ? (
        <div
          className={`flex items-center justify-center w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10 rounded-full ${isRacing ? 'bg-white' : 'bg-white/20'}`}
          aria-hidden="true"
        >
          <i
            className={`bi ${
              sport_id === 10 || sport_id === 11
                ? 'bi-lightning-charge'
                : sport_id === 20 || sport_id === 21
                  ? 'bi-trophy'
                  : sport_id === 40
                    ? 'bi-activity'
                    : sport_id === 50 || sport_id === 51
                      ? 'bi-dribbble'
                      : 'bi-joystick'
            } text-base md:text-lg ${sport_id === 50 || sport_id === 51 ? 'text-gray-900' : 'text-white'}`}
            style={isRacing && sport_id != null ? { color: sportColor } : undefined}
            aria-hidden="true"
          />
        </div>
      ) : isRacing ? (
        <div className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10 rounded-full bg-white overflow-hidden flex-shrink-0">
          <img
            src={iconSrc}
            alt={buttonLabel}
            className="w-6 h-6 md:w-7 md:h-7 xl:w-8 xl:h-8 object-contain"
            onError={() => setIconErrorMap((prev) => ({ ...prev, [iconKey]: true }))}
          />
        </div>
      ) : (
        <div className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10 flex-shrink-0 overflow-hidden">
          <img
            src={iconSrc}
            alt={buttonLabel}
            className="w-full h-full object-contain"
            onError={() => setIconErrorMap((prev) => ({ ...prev, [iconKey]: true }))}
          />
        </div>
      )}
      <div className="flex flex-col items-center md:items-start leading-tight shrink-0 overflow-visible">
        <span className={`max-md:hidden md:block text-sm xl:text-base whitespace-nowrap ${isActive ? 'font-bold' : 'font-medium'}`}>
          {desktopLabel}
        </span>
        <div className="mt-0.5 md:mt-1 flex-shrink-0 flex flex-col items-center md:items-start">
          <span className="md:hidden text-xs font-semibold tabular-nums min-w-[1ch]">
            {isRacing && Number(n_runners) > 0 ? n_runners : '\u00A0'}
          </span>
          {phase === 'live' ? (
            <div className="flex items-center justify-center md:justify-start gap-1 animate-pulse">
              <span className={`text-2xs md:text-sm font-bold ${sport_id === 50 || sport_id === 51 ? 'text-red-900' : 'text-red-100'}`}>
                LIVE
              </span>
            </div>
          ) : phase === 'starting' ? (
            <div className={`flex items-center justify-center md:justify-start gap-1 ${sport_id === 50 || sport_id === 51 ? 'text-yellow-900' : 'text-yellow-100'}`}>
              <span className="spinner-border spinner-border-sm" aria-hidden />
            </div>
          ) : (
            <span className={`text-2xs md:text-sm font-semibold whitespace-nowrap ${sport_id === 50 || sport_id === 51 ? 'text-gray-900' : 'text-white'}`}>
              {countdownSeconds != null && countdownSeconds > 0 ? formatCountdown(countdownSeconds) : '--:--'}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * Schedule selector bar: one button per schedule.
 * Uses schedule.label from API; key and selection by schedule_id.
 */
function ScheduleSelectorBar() {
  const { t } = useTranslation();
  const { data: schedules = [], isLoading: loading } = useSchedulesQuery();
  const { currentScheduleId: current_schedule_id, setCurrentScheduleId } = useScheduleContext();
  const pruneTipsByEventIds = useTicketSlipStore((s) => s.pruneTipsByEventIds);
  const { switchToBets } = useMobileTabsContext();
  const lastPruneKeyRef = React.useRef<string>('');
  const [iconErrorMap, setIconErrorMap] = React.useState<Record<string, boolean>>({});

  const handleScheduleClick = (schedule_id: number | string) => {
    if (schedule_id === current_schedule_id) return;
    setCurrentScheduleId(schedule_id);
    switchToBets();
  };

  const available_schedules = React.useMemo(
    () =>
      (Array.isArray(schedules) ? schedules : []).filter((schedule) => {
        const sportObj = schedule.sport;
        return (
          sportObj && (sportObj.active === true || sportObj.active === 1 || parseInt(String(sportObj.active)) === 1)
        );
      }),
    [schedules]
  );

  const sortedSchedules = React.useMemo(() => {
    const list = [...available_schedules];
    return list.sort((a, b) => {
      const aSportId = a.sport?.sport_id != null ? Number(a.sport.sport_id) : 0;
      const bSportId = b.sport?.sport_id != null ? Number(b.sport.sport_id) : 0;
      if (aSportId !== bSportId) return aSportId - bSportId;
      const aRunners = a.sport?.n_runners != null ? Number(a.sport.n_runners) : 0;
      const bRunners = b.sport?.n_runners != null ? Number(b.sport.n_runners) : 0;
      if (aRunners !== bRunners) return aRunners - bRunners;
      const aDate = (a as { schedule_date?: string }).schedule_date ?? '';
      const bDate = (b as { schedule_date?: string }).schedule_date ?? '';
      return String(aDate).localeCompare(String(bDate));
    });
  }, [available_schedules]);

  const allUpcomingQueries = useAllUpcomingEventsQueries({ schedules: available_schedules as Schedule[], numEvents: UPCOMING_EVENTS_LIST_SIZE });

  React.useEffect(() => {
    const eventIds = new Set<string | number>();
    for (const q of allUpcomingQueries) {
      const events = flattenScheduleUpcomingToEvents(q.data);
      for (const ev of events) eventIds.add(ev.event_id);
    }
    if (eventIds.size > 0) {
      const ids = Array.from(eventIds);
      const key = ids.map(String).sort().join(',');
      if (key !== lastPruneKeyRef.current) {
        lastPruneKeyRef.current = key;
        pruneTipsByEventIds(ids);
      }
    }
  }, [allUpcomingQueries, pruneTipsByEventIds]);

  React.useEffect(() => {
    if (loading) return;
    if (!available_schedules.length) return;
    const currentScheduleExists = available_schedules.some((sched) => sched.schedule_id === current_schedule_id);
    if (!current_schedule_id || !currentScheduleExists) {
      setCurrentScheduleId(available_schedules[0].schedule_id);
      switchToBets();
    }
  }, [loading, available_schedules, current_schedule_id, setCurrentScheduleId, switchToBets]);

  const hoverStyles = React.useMemo(() => {
    const sportIds = Array.from(
      new Set(
        sortedSchedules
          .map((s) => s.sport?.sport_id)
          .filter((id): id is number => id != null && Number.isFinite(Number(id)))
          .map(Number)
      )
    );
    return sportIds
      .map((sportId) => {
        const color = getSportColor(sportId);
        const onColor = getSportOnColor(sportId);
        return `
        .schedule-selector-bar button[data-sport-id="${sportId}"]:hover:not([data-active="true"]) {
          background-color: ${color} !important;
          color: ${onColor} !important;
          opacity: 1 !important;
        }
      `;
      })
      .join('\n');
  }, [sortedSchedules]);

  const sportIdsWithLocalPngIcon = new Set<number>([10, 11, 20, 21, 30, 40]);

  return (
    <div
      className="schedule-selector-bar relative w-full z-50 bg-card/95 supports-[backdrop-filter]:bg-card/80 backdrop-blur border-b border-border h-[100px] md:h-[84px]"
    >
      <style>{hoverStyles}</style>
      <DynamicSpinnerComp status={loading} />
      <div
        className="overflow-x-auto overflow-y-hidden scroll-smooth h-full"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          direction: 'ltr',
          scrollPaddingLeft: '0px',
          scrollPaddingRight: '0px',
        }}
      >
        <style>{`
          .schedule-selector-bar div::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        <div
          className="flex items-stretch h-full w-full min-h-0"
          style={{ paddingLeft: '0px', paddingRight: '0px' }}
        >
          {sortedSchedules.length > 0 ? (
            sortedSchedules.map((schedule, index) => (
              <ScheduleButtonItem
                key={String(schedule.schedule_id)}
                schedule={schedule}
                isActive={String(schedule.schedule_id) === String(current_schedule_id)}
                isLast={index === sortedSchedules.length - 1}
                iconErrorMap={iconErrorMap}
                setIconErrorMap={setIconErrorMap}
                onScheduleClick={handleScheduleClick}
                sportIdsWithLocalPngIcon={sportIdsWithLocalPngIcon}
              />
            ))
          ) : (
            <div className="px-5 py-3 text-muted-foreground text-sm">
              {t('NO_SPORTS_AVAILABLE') || 'No sports available'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScheduleSelectorBar;
